import React, { useState, useEffect } from 'react';
import type { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';

export type InboxMessageType = 
  | 'order_filled' 
  | 'territory_lost' 
  | 'courier_expired' 
  | 'quest_complete' 
  | 'system';

export interface InboxMessage {
  id: string;
  characterId: string;
  messageType: InboxMessageType;
  title: string;
  message: string;
  metadata: Record<string, any>;
  isRead: boolean;
  createdAt: string;
  expiresAt?: string;
}

interface PlayerInboxProps {
  socket: Socket | null;
  onClose: () => void;
}

export const PlayerInbox: React.FC<PlayerInboxProps> = ({ socket, onClose }) => {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<InboxMessage | null>(null);
  const { position, handleHeaderMouseDown } = useDraggablePanel(100, 100, { storageKey: 'inbox-panel-position' });

  useEffect(() => {
    if (!socket) return;

    socket.emit('inbox:getMessages', { unreadOnly: showUnreadOnly, limit: 50 });
    socket.emit('inbox:getUnreadCount');

    const handleMessages = (data: { messages: InboxMessage[] }): void => {
      setMessages(data.messages);
    };

    const handleUnreadCount = (data: { count: number }): void => {
      setUnreadCount(data.count);
    };

    const handleMessageRead = (data: { messageId: string }): void => {
      setMessages(prev => prev.map(msg => 
        msg.id === data.messageId ? { ...msg, isRead: true } : msg
      ));
    };

    const handleMessageDeleted = (data: { messageId: string }): void => {
      setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
      if (selectedMessage?.id === data.messageId) {
        setSelectedMessage(null);
      }
    };

    const handleAllMarkedRead = (): void => {
      setMessages(prev => prev.map(msg => ({ ...msg, isRead: true })));
    };

    const handleAllReadDeleted = (): void => {
      setMessages(prev => prev.filter(msg => !msg.isRead));
      if (selectedMessage?.isRead) {
        setSelectedMessage(null);
      }
    };

    socket.on('inbox:messages', handleMessages);
    socket.on('inbox:unreadCount', handleUnreadCount);
    socket.on('inbox:messageRead', handleMessageRead);
    socket.on('inbox:messageDeleted', handleMessageDeleted);
    socket.on('inbox:allMarkedRead', handleAllMarkedRead);
    socket.on('inbox:allReadDeleted', handleAllReadDeleted);

    return () => {
      socket.off('inbox:messages', handleMessages);
      socket.off('inbox:unreadCount', handleUnreadCount);
      socket.off('inbox:messageRead', handleMessageRead);
      socket.off('inbox:messageDeleted', handleMessageDeleted);
      socket.off('inbox:allMarkedRead', handleAllMarkedRead);
      socket.off('inbox:allReadDeleted', handleAllReadDeleted);
    };
  }, [socket, showUnreadOnly, selectedMessage]);

  const handleMarkAsRead = (messageId: string): void => {
    socket?.emit('inbox:markAsRead', { messageId });
  };

  const handleMarkAllAsRead = (): void => {
    socket?.emit('inbox:markAllAsRead');
  };

  const handleDeleteMessage = (messageId: string): void => {
    socket?.emit('inbox:deleteMessage', { messageId });
  };

  const handleDeleteAllRead = (): void => {
    socket?.emit('inbox:deleteAllRead');
  };

  const handleSelectMessage = (message: InboxMessage): void => {
    setSelectedMessage(message);
    if (!message.isRead) {
      handleMarkAsRead(message.id);
    }
  };

  const getMessageTypeLabel = (type: InboxMessageType): string => {
    switch (type) {
      case 'order_filled': return 'ORDER';
      case 'territory_lost': return 'TERRITORY';
      case 'courier_expired': return 'COURIER';
      case 'quest_complete': return 'QUEST';
      case 'system': return 'SYSTEM';
      default: return 'MESSAGE';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div
      className="font-seurat"
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: '90%',
        maxWidth: 800,
        height: '80%',
        maxHeight: 600,
        background: '#0d1117',
        border: '2px solid #30363d',
        borderRadius: 0,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #21262d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: '#c9d1d9' }}>
            INBOX
          </span>
          {unreadCount > 0 && (
            <span
              style={{
                background: '#ef4444',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 0,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#8b949e',
            fontSize: 20,
            cursor: 'pointer',
            padding: 4,
          }}
        >
          X
        </button>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div
          style={{
            width: selectedMessage ? '40%' : '100%',
            borderRight: selectedMessage ? '1px solid #21262d' : 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #21262d',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <button
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              style={{
                padding: '6px 12px',
                background: showUnreadOnly ? '#238636' : '#21262d',
                border: '1px solid #30363d',
                borderRadius: 0,
                color: '#c9d1d9',
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {showUnreadOnly ? 'UNREAD' : 'ALL'}
            </button>
            <button
              onClick={handleMarkAllAsRead}
              style={{
                padding: '6px 12px',
                background: '#21262d',
                border: '1px solid #30363d',
                borderRadius: 0,
                color: '#c9d1d9',
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              MARK READ
            </button>
            <button
              onClick={handleDeleteAllRead}
              style={{
                padding: '6px 12px',
                background: '#21262d',
                border: '1px solid #30363d',
                borderRadius: 0,
                color: '#c9d1d9',
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              DELETE READ
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {messages.length === 0 ? (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  color: '#6e7681',
                  fontSize: 13,
                }}
              >
                {showUnreadOnly ? 'No unread messages' : 'No messages'}
              </div>
            ) : (
              messages.map(message => (
                <div
                  key={message.id}
                  onClick={() => handleSelectMessage(message)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #21262d',
                    cursor: 'pointer',
                    background: selectedMessage?.id === message.id
                      ? '#161b22'
                      : message.isRead
                      ? 'transparent'
                      : '#0d1b2a',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMessage?.id !== message.id) {
                      e.currentTarget.style.background = '#161b22';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMessage?.id !== message.id) {
                      e.currentTarget.style.background = message.isRead ? 'transparent' : '#0d1b2a';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: '#6e7681',
                        minWidth: 50,
                      }}
                    >
                      {getMessageTypeLabel(message.messageType)}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: message.isRead ? 400 : 600,
                        color: message.isRead ? '#8b949e' : '#c9d1d9',
                        flex: 1,
                      }}
                    >
                      {message.title}
                    </span>
                    {!message.isRead && (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 0,
                          background: '#3b82f6',
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6e7681',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {message.message}
                  </div>
                  <div style={{ fontSize: 10, color: '#6e7681', marginTop: 4 }}>
                    {formatTimestamp(message.createdAt)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {selectedMessage && (
          <div
            style={{
              width: '60%',
              display: 'flex',
              flexDirection: 'column',
              background: '#0d1117',
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #21262d',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#6e7681',
                    minWidth: 50,
                  }}
                >
                  {getMessageTypeLabel(selectedMessage.messageType)}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#c9d1d9' }}>
                  {selectedMessage.title}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#6e7681' }}>
                {formatTimestamp(selectedMessage.createdAt)}
              </div>
            </div>

            <div
              style={{
                flex: 1,
                padding: '20px',
                overflowY: 'auto',
                color: '#c9d1d9',
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              {selectedMessage.message}
            </div>

            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #21262d',
                display: 'flex',
                gap: 8,
              }}
            >
              <button
                onClick={() => handleDeleteMessage(selectedMessage.id)}
                style={{
                  padding: '8px 16px',
                  background: '#da3633',
                  border: '1px solid #f85149',
                  borderRadius: 0,
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                }}
              >
                DELETE
              </button>
              <button
                onClick={() => setSelectedMessage(null)}
                style={{
                  padding: '8px 16px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 0,
                  color: '#c9d1d9',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
